/**
 * Kontroll V7.5.6 (#275): what each registration is in the review, decided once so the summary, the
 * queue, the exception filters, Per ansatt and approve-and-next cannot disagree. close.ts still decides
 * the shift's kind (the business rule); this adds the review buckets, the exceptions a record carries,
 * the per-person counts and the completion rule.
 *
 * Buckets: approved; ongoing (started, not ended: someone at work, never in an approval bucket); planned
 * (not started); review (a missing or open punch, a deviation, an overlap or invalid time, or an open
 * question to the employee); ready (the rest). Waiting for the employee is a subset of review, never a
 * bucket of its own. In this data model the plan and the record are one shift row, so a completed
 * expected shift with no clocking at all is a gap (V7.5.6 AC-13): not a registration, in no approval
 * bucket, its hours unknown, not zero. A gap on a day the person has approved absence is accounted for.
 *
 * Exceptions: a record carries every one that applies; a filter matches any of them; the row shows the
 * first in EXCEPTIONS order.
 */
import type { ApiAbsence, ApiShift } from '@/api/data';
import { oslo } from '@/rules';
import { type Kind, kindOf, needsAction, noBreak } from './close';
import type { Block } from './review';

export type Bucket = 'review' | 'ready' | 'approved' | 'ongoing' | 'planned' | 'gap' | 'accounted';
export type Exception =
  | 'missing'
  | 'invalid'
  | 'overlap'
  | 'long'
  | 'pause'
  | 'deviation'
  | 'unplanned'
  | 'waiting';
/** the exception filter's order and words, as the prototype (V7.5.6); «Mangler stempling» covers a missing clock-out too */
export const EXCEPTIONS: Exception[] = [
  'missing',
  'pause',
  'long',
  'deviation',
  'invalid',
  'overlap',
  'unplanned',
  'waiting',
];
export const EXCEPTION_TEXT: Record<Exception, string> = {
  missing: 'Mangler stempling',
  pause: 'Pause',
  long: 'Lang registrering',
  deviation: 'Avvik fra plan',
  invalid: 'Ugyldige tider',
  overlap: 'Overlapper en annen registrering',
  unplanned: 'Uten plan',
  waiting: 'Venter på ansatt',
};

export interface Ctx {
  now: number;
  tol: number;
  blocks: Map<number, Block>;
  /** shift ids with an open question to the employee */
  waiting: Set<number>;
  /** over 13 h of working time on the day (core's day_over_13, aml § 10-6 tiende ledd) */
  long: (s: ApiShift) => boolean;
  /** the unpaid break takes every recorded minute */
  pauseCovers: (s: ApiShift) => boolean;
  /** registered with no plan: the company's «Stemplet uten plan» type */
  unplanned: (s: ApiShift) => boolean;
  /** shift ids whose clock or break times were corrected (work-time history «changed») */
  corrected?: Set<number>;
  /** the person has approved absence over the shift: a gap there is accounted for, not missing work */
  absent?: (s: ApiShift) => boolean;
}

export interface Rec {
  s: ApiShift;
  kind: Kind;
  block: Block | undefined;
  bucket: Bucket;
  exceptions: Exception[];
  /** what the record is, in the prototype's words: the row's status and the panel's case */
  problem: Problem;
  corrected: boolean;
}

export interface Problem {
  label: string;
  text: string;
  /** «Avvik først» sorts on this: 4 invalid, multi-day, pause covering the shift; 3 missing; 2 others; 0 none */
  severity: number;
}

const at = (iso: string | null | undefined) => (iso ? Date.parse(iso) : Number.NaN);
/** in or out further from the plan than the tolerance */
export const offPlan = (s: ApiShift, tol: number) =>
  !!s.punch_in_time &&
  !!s.punch_out_time &&
  (Math.abs(at(s.punch_in_time) - at(s.start_time)) > tol * 60000 ||
    Math.abs(at(s.punch_out_time) - at(s.end_time)) > tol * 60000);

export function classify(s: ApiShift, c: Ctx): Rec {
  const kind = kindOf(s, c.now, c.tol);
  const block = c.blocks.get(s.id);
  const waiting = !s.is_approved && c.waiting.has(s.id);
  const bucket: Bucket =
    kind === 'approved'
      ? 'approved'
      : kind === 'running'
        ? 'ongoing'
        : kind === 'future'
          ? 'planned'
          : kind === 'missing'
            ? c.absent?.(s)
              ? 'accounted'
              : 'gap'
            : needsAction(kind) || block || waiting
              ? 'review'
              : 'ready';
  const exceptions: Exception[] = [];
  if (bucket === 'review' || bucket === 'ready' || bucket === 'gap') {
    const has: Record<Exception, boolean> = {
      missing: kind === 'missing' || kind === 'open',
      invalid: block === 'invalid',
      overlap: block === 'overlap',
      waiting,
      long: !!s.punch_in_time && !!s.punch_out_time && (c.long(s) || multiDay(s)),
      pause: noBreak(s) || c.pauseCovers(s),
      deviation: offPlan(s, c.tol),
      unplanned: c.unplanned(s),
    };
    for (const e of EXCEPTIONS) if (has[e]) exceptions.push(e);
  }
  return {
    s,
    kind,
    block,
    bucket,
    exceptions,
    problem: problemOf(s, kind, block, c),
    corrected: !!c.corrected?.has(s.id),
  };
}

export const matches = (r: Rec, f: Exception | null) => f == null || r.exceptions.includes(f);
/** an exception from the address; the old Oversikt links say «uten» and «open» for missing punches */
export const exceptionOf = (v: string | null): Exception | null =>
  v === 'uten' || v === 'open'
    ? 'missing'
    : v && (EXCEPTIONS as string[]).includes(v)
      ? (v as Exception)
      : null;

const minutes = (a: string | null | undefined, b: string | null | undefined) =>
  Math.round((at(b) - at(a)) / 60000);
/** clocked over more than a day: the prototype's «Stempling over flere dager» */
export const multiDay = (s: ApiShift) =>
  !!s.punch_in_time && !!s.punch_out_time && minutes(s.punch_in_time, s.punch_out_time) > 24 * 60;
const dayNum = (iso: string) =>
  Number(new Intl.DateTimeFormat('nb-NO', { timeZone: 'Europe/Oslo', day: 'numeric' }).format(new Date(iso)));

/** The record's case in the prototype's words, the first that applies by severity. */
export function problemOf(s: ApiShift, kind: Kind, block: Block | undefined, c: Ctx): Problem {
  if (kind === 'missing')
    return {
      label: 'Mangler stempling',
      text: 'Ingen inn- eller utstempling. Bekreft faktisk arbeidstid.',
      severity: 3,
    };
  if (kind === 'open')
    return {
      label: 'Mangler utstempling',
      text: 'Sluttid mangler. Arbeidstiden er ikke avklart.',
      severity: 3,
    };
  if (block === 'invalid')
    return { label: 'Ugyldig tidsrom', text: 'Kontroller datoer, klokkeslett og pausetrekk.', severity: 4 };
  if (block === 'overlap')
    return {
      label: 'Overlapper en annen registrering',
      text: 'Ligger over en annen registrering for samme person. Rett eller slett den ene før godkjenning.',
      severity: 4,
    };
  if (kind === 'approved' || kind === 'running' || kind === 'future')
    return { label: 'Samsvarer med plan', text: 'Registrering og pause er avklart.', severity: 0 };
  if (multiDay(s))
    return {
      label: 'Stempling over flere dager',
      text: `Registreringen går fra ${dayNum(s.punch_in_time as string)}. til ${dayNum(s.punch_out_time as string)}. Kontroller begge datoene.`,
      severity: 4,
    };
  if (c.pauseCovers(s)) {
    const g = minutes(s.punch_in_time, s.punch_out_time);
    return {
      label: 'Pause dekker hele vakten',
      text: `${g} min registrert og ${g} min pausetrekk gir 0 t arbeidstid.`,
      severity: 4,
    };
  }
  if (c.long(s))
    return {
      label: 'Lang registrering',
      text: 'Over 13 timer arbeidstid på ett døgn (arbeidsmiljøloven § 10-6 tiende ledd).',
      severity: 2,
    };
  if (noBreak(s))
    return {
      label: 'Ingen pause registrert',
      // under «stemplet, minst» the minimum still comes off, and the case says so (plan 0032)
      text:
        s.break_mode === 'punched_min'
          ? 'Ingen pause registrert, minimumspausen trekkes. Bekreft hva som faktisk ble tatt.'
          : 'Planen har pause. Bekreft hva som faktisk ble tatt.',
      severity: 2,
    };
  if (c.unplanned(s))
    return {
      label: 'Registrert uten plan',
      text: 'Bekreft arbeidstiden. Ingen planlagt vakt er knyttet til registreringen.',
      severity: 2,
    };
  if (offPlan(s, c.tol)) {
    const out = minutes(s.end_time, s.punch_out_time);
    const inn = minutes(s.start_time, s.punch_in_time);
    const useOut = Math.abs(out) > c.tol;
    const d = useOut ? out : inn;
    return {
      label: 'Avvik fra plan',
      text: `Stemplet ${useOut ? 'ut' : 'inn'} ${Math.abs(d)} min ${d > 0 ? 'etter' : 'før'} plan.`,
      severity: 2,
    };
  }
  return { label: 'Samsvarer med plan', text: 'Registrering og pause er avklart.', severity: 0 };
}

/** «Avvik først»: the highest severity first, then the date, as the prototype */
export const bySeverity = (a: Rec, b: Rec) =>
  b.problem.severity - a.problem.severity || a.s.start_time.localeCompare(b.s.start_time);

export interface Summary {
  review: number;
  ready: number;
  approved: number;
  /** review + ready + approved: the completed records the three buckets divide */
  total: number;
  ongoing: number;
  waiting: number;
  /** review records whose worked hours cannot be known yet (an open punch, invalid times) */
  unknown: number;
  approvedHours: number;
  /** completed expected shifts with no registration: outside the three buckets (AC-13) */
  gaps: number;
  /** such shifts on a day of approved absence: accounted for */
  accounted: number;
}

export function summarize(recs: Rec[], hoursOf: (s: ApiShift) => number | null): Summary {
  const o: Summary = {
    review: 0,
    ready: 0,
    approved: 0,
    total: 0,
    ongoing: 0,
    waiting: 0,
    unknown: 0,
    approvedHours: 0,
    gaps: 0,
    accounted: 0,
  };
  for (const r of recs) {
    if (r.bucket === 'ongoing') o.ongoing++;
    if (r.bucket === 'gap') o.gaps++;
    if (r.bucket === 'accounted') o.accounted++;
    if (r.bucket !== 'review' && r.bucket !== 'ready' && r.bucket !== 'approved') continue;
    o[r.bucket]++;
    o.total++;
    if (r.exceptions.includes('waiting')) o.waiting++;
    const h = hoursOf(r.s);
    if (r.bucket === 'review' && h == null) o.unknown++;
    if (r.bucket === 'approved' && h != null) o.approvedHours += h;
  }
  return o;
}

export interface PersonRow {
  id: number;
  review: number;
  ready: number;
  approved: number;
  total: number;
  ongoing: number;
  waiting: number;
  missing: number;
  /** completed expected shifts with no registration and no approved absence */
  gaps: number;
  /**
   * «Ferdig kontrollert»: at least one completed record, every completed record approved, nobody at work on
   * a record, and no expected shift left without a registration or approved absence. Zero records is never
   * complete. It says nothing about payroll transfer.
   */
  complete: boolean;
}

/** One row per person with a record in the scope, future-only people included with zeros. */
export function perPerson(recs: Rec[]): PersonRow[] {
  const by = new Map<number, PersonRow>();
  for (const r of recs) {
    const id = r.s.user_id;
    if (id == null) continue;
    const p =
      by.get(id) ??
      ({
        id,
        review: 0,
        ready: 0,
        approved: 0,
        total: 0,
        ongoing: 0,
        waiting: 0,
        missing: 0,
        gaps: 0,
        complete: false,
      } as PersonRow);
    by.set(id, p);
    if (r.bucket === 'ongoing') p.ongoing++;
    if (r.bucket === 'gap') p.gaps++;
    if (r.bucket === 'review' || r.bucket === 'ready' || r.bucket === 'approved') {
      p[r.bucket]++;
      p.total++;
    }
    if (r.exceptions.includes('waiting')) p.waiting++;
    if (r.exceptions.includes('missing')) p.missing++;
  }
  for (const p of by.values())
    p.complete = p.total > 0 && p.review === 0 && p.ready === 0 && p.ongoing === 0 && p.gaps === 0;
  return [...by.values()];
}

/**
 * «Mest å avklare»: people with work left first, then the most records needing review plus expected shifts
 * with no registration, then the most ready, then the name. «Navn»: the name alone, Norwegian order.
 */
export function sortPeople(
  rows: PersonRow[],
  by: 'prioritet' | 'navn',
  nameOf: (id: number) => string,
): PersonRow[] {
  const name = (a: PersonRow, b: PersonRow) => nameOf(a.id).localeCompare(nameOf(b.id), 'nb');
  return [...rows].sort((a, b) =>
    by === 'navn'
      ? name(a, b)
      : Number(a.complete) - Number(b.complete) ||
        b.review + b.gaps - (a.review + a.gaps) ||
        b.ready - a.ready ||
        name(a, b),
  );
}

/**
 * The next record to open in a queue captured when the panel opened: after the current one, then from the
 * start, skipping what `eligible` refuses (approved, waiting, gone). Null when nothing is left. Opening a
 * record changes nothing.
 */
export function nextIn(queue: number[], current: number, eligible: (id: number) => boolean): number | null {
  const i = queue.indexOf(current);
  const order = [...queue.slice(i + 1), ...queue.slice(0, Math.max(i, 0))];
  return order.find((id) => id !== current && eligible(id)) ?? null;
}

/**
 * The person has approved absence over the planned shift: a whole-day type on its Oslo date, a type with
 * clock times when the two overlap. Any approved type counts (sick leave, vacation, unpaid leave): the shift
 * is accounted for without a clocking; what it pays stays the absence's own rule.
 */
export function absentOn(absences: ApiAbsence[], s: ApiShift): boolean {
  const day = oslo(s.start_time)?.date ?? '';
  return absences.some(
    (a) =>
      a.user_id === s.user_id &&
      a.status === 'accepted' &&
      (['time', 'datetime'].includes(a.absence_type?.type ?? '')
        ? Date.parse(a.start_date) < Date.parse(s.end_time) &&
          Date.parse(a.end_date) > Date.parse(s.start_time)
        : (oslo(a.start_date)?.date ?? '') <= day && day <= (oslo(a.end_date)?.date ?? '')),
  );
}
